import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useReportingStore } from '@/stores/reporting-store'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts'

interface ActivityChartsProps {
    className?: string
}

export function ActivityCharts({ className = '' }: ActivityChartsProps) {
    const { t } = useTranslation()
    const { callOverview } = useReportingStore()

    // Clean color palette - only purple/black tones
    const colors = {
        primary: '#6F59FD',    // Brand purple
        secondary: '#9333EA',  // Purple-700
        tertiary: '#A855F7',   // Purple-600
        dark: '#1F2937',       // Gray-800
        light: '#6B7280',      // Gray-500
    }

    // Process ONLY real data from API - no simulations
    const chartData = useMemo(() => {
        const states = callOverview?.conversationsByState || {}

        const successfulCalls = states.BUSINESSDATA_DONE?.length || 0

        // Call status distribution - ONLY real API data
        const inProgress = (states.WAITING_FOR_BUSINESSDATA?.length || 0) + (states.WAITING_FOR_USER_INPUT?.length || 0)
        const otherStates = Object.values(states).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0) - successfulCalls - inProgress

        const statusData = [
            {
                status: t('activityCharts.successful'),
                anzahl: successfulCalls,
                fill: colors.primary
            },
            {
                status: t('activityCharts.inProgress'),
                anzahl: inProgress,
                fill: colors.secondary
            },
            {
                status: t('activityCharts.other'),
                anzahl: Math.max(otherStates, 0),
                fill: colors.tertiary
            }
        ].filter(item => item.anzahl > 0) // Only show categories with data

        return { statusData, hasData: successfulCalls > 0 || inProgress > 0 || otherStates > 0 }
    }, [callOverview, colors])

    // Show "no data" message if no real data available
    if (!callOverview || !chartData.hasData) {
        return (
            <div className={`${className}`}>
                <Card>
                    <CardContent className="p-12 text-center">
                        <div className="text-gray-400 mb-2">
                            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                        </div>
                        <p className="text-gray-600 font-medium">{t('activityCharts.noData')}</p>
                        <p className="text-sm text-gray-500 mt-2">
                            {t('activityCharts.noDataHint')}
                        </p>
                    </CardContent>
                </Card>
            </div>
        )
    }

    return (
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-4 ${className}`}>
            {/* Call Status Pie Chart - ONLY REAL DATA */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-sm font-medium text-gray-900">{t('activityCharts.callStatusDistribution')}</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={chartData.statusData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={40}
                                    outerRadius={80}
                                    dataKey="anzahl"
                                    label={({ status, anzahl }) => `${status}: ${anzahl}`}
                                    labelLine={false}
                                >
                                    {chartData.statusData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.fill} />
                                    ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>

            {/* Summary Card - ONLY REAL DATA */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-sm font-medium text-gray-900">{t('activityCharts.summary')}</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-600">{t('activityCharts.completedSuccessfully')}:</span>
                            <span className="text-sm font-medium">{callOverview?.conversationsByState?.BUSINESSDATA_DONE?.length || 0}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-600">{t('activityCharts.inProgress')}:</span>
                            <span className="text-sm font-medium">
                                {(callOverview?.conversationsByState?.WAITING_FOR_BUSINESSDATA?.length || 0) + (callOverview?.conversationsByState?.WAITING_FOR_USER_INPUT?.length || 0)}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-sm text-gray-600">{t('activityCharts.totalToday')}:</span>
                            <span className="text-sm font-medium">
                                {Object.values(callOverview?.conversationsByState || {}).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0)}
                            </span>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}